import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled, { keyframes } from 'styled-components';
import { patch as updatePhoneNumber } from 'actions/phone-number-update';
import { closeModal } from 'actions/modal';
import { Separator, Text, Button } from '@cuemath/comps';
import COLORS from 'constants/colors';
import { phoneNumberUpdates, FIELD_TYPES, getOnChangeMethod } from 'constants/form';
import { FieldWrapper, renderFormField, getOptions, Head } from 'modules/form';
import { LoaderAlt } from 'styled-icons/boxicons-regular/LoaderAlt';
import { CheckCircle } from 'styled-icons/boxicons-regular/CheckCircle';
import crossCircle from 'img/white-cross-icon.svg';

import { EVENT_TYPE } from 'constants/logger';
import { trackEvent } from 'modules/analytics';

const { INPUT, SELECT } = FIELD_TYPES;

const CrossIcon = styled.div`
  position: absolute;
  cursor: pointer;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  right: -44px;
  top: -35px;
`;

const Wrapper = styled.div`
  padding-right: 10px;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

const StyledButton = styled(Button)`
  margin: 20px auto;
  width: 130px;
`;

const StyledText = styled(Text)`
  margin-left: 50px;
`;

const LoaderIcon = styled(LoaderAlt)`
  margin: 40px auto;
  height: 140px;
  animation: ${spin} 1s linear infinite;
`;

const CheckCircleIcon = styled(CheckCircle)`
  margin: 40px auto;
  height: 140px;
  animation: ${fadeIn} 0.5s linear;
`;

class EditStudentPhoneContainer extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      updatePhoneNumber: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired,
    }).isRequired,
    onUpdate: PropTypes.func.isRequired,
    student: PropTypes.shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: '',
      form: this.getFormDefaults(),
      phonePatch: store.patch(),
      phoneUpdateFormFields: this.getFormFields(),
      showSuccess: false,
      showLoader: false,
    };
    this.assignChangeHandlers(phoneNumberUpdates);
  }

  static getDerivedStateFromProps(props, state) {
    const { phoneUpdate } = props;
    const { showLoader, showSuccess, phonePatch } = state;
    const phonePatchState = store.patch(phoneUpdate, phonePatch.id);
    const { current, resolved } = phonePatchState || {};
    const { isPatching } = current || {};
    const { isPatched, isPatchingFailed } = resolved || {};
    const newState = {};

    if (resolved) {
      if (isPatchingFailed) {
        if (store.getError(resolved)) {
          newState.errorText = store.getError(resolved).message;
          if (showLoader) newState.showLoader = false;
        } else {
          newState.errorText = 'Error';
        }
      }
      newState.phonePatch = phonePatchState;
    }

    if ((isPatching || isPatched) && !showLoader && !showSuccess) {
      // want to show loader also till 2 sec after isPatched.
      newState.showLoader = true;
    }

    return newState;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      phonePatch: { resolved },
    } = this.state;
    const { isPatched } = resolved || {};
    const {
      phonePatch: { resolved: prevResolved },
    } = prevState;
    const { isPatched: prevPatched } = prevResolved || {};

    if (isPatched && !prevPatched) {
      setTimeout(this.phoneUpdateSuccess, 2000);
    }
  }

  assignChangeHandlers = formFields => {
    formFields.forEach(({ name, fieldType }) => {
      const methodName = getOnChangeMethod(name);
      if (fieldType === INPUT) {
        this[methodName] = this.onChange.bind(this, name);
      } else {
        this[methodName] = this.onChange.bind(this);
      }
    });
  };

  getFormFields = () => {
    return phoneNumberUpdates.map(field => {
      const { fieldType, name } = field || {};
      if (fieldType === SELECT) field.choices = getOptions({ field: name });
      return field;
    });
  };

  getFormDefaults = () => {
    const { student } = this.props;
    const { country_code: countryCode = '', phone = '' } = student || {};

    return { countryCode, phone };
  };

  onChange = (key, value) => {
    const { form, errorText } = this.state;

    this.setState(() => ({
      form: {
        ...form,
        [key]: value,
      },
    }));

    if (errorText) this.clearMesssage();
  };

  clearMesssage = () => {
    this.setState(() => ({ errorText: null }));
  };

  validatePhoneNumber = () => {
    // basic phone length check...there's validation in the backend.
    const {
      form: { countryCode, phone },
    } = this.state;
    if (countryCode === '91' && phone.length !== 10) {
      return false;
    }
    if (phone.length < 4) {
      return false;
    }
    return true;
  };

  handlePatchAction = () => {
    const { actions, student } = this.props;
    const {
      form: { countryCode, phone },
      phonePatch: { id },
    } = this.state;

    if (!this.validatePhoneNumber()) {
      this.setState({ errorText: 'Invalid Phone Number' });
      return;
    }
    trackEvent(EVENT_TYPE.EDIT_PHONE_NUMBER_SUBMIT_CLICKED, { student_id: student.id });

    actions.updatePhoneNumber(id, student.id, { country_code: countryCode, phone });
  };

  phoneUpdateSuccess = () => {
    const { onUpdate } = this.props;
    this.setState({ showSuccess: true, showLoader: false });
    onUpdate();
  };

  render() {
    const {
      errorText,
      showSuccess,
      form,
      showLoader,
      phoneUpdateFormFields,
    } = this.state;
    const { countryCode, phone } = form;
    const { actions, student } = this.props;
    const { country_code: currentCountryCode, phone: currentPhone } = student || {};
    const isButtonDisabled = countryCode === currentCountryCode && phone === currentPhone;

    return (
      <Wrapper>
        {!showLoader && (
          <CrossIcon onClick={actions.closeModal}>
            <img src={crossCircle} width="32" height="32" alt="closed-icon" />
          </CrossIcon>
        )}
        <Head>
          <Text size="xlarge" align="center">
            Edit Phone Number
          </Text>
        </Head>
        <Separator transparent height="20" />
        <Separator height="2" />
        <Separator transparent height="25" />

        {showLoader && (
          <>
            <LoaderIcon color={COLORS.BOSTON_BLUE} />
          </>
        )}
        {showSuccess && <CheckCircleIcon color={COLORS.DARK_GREEN} />}
        {!showLoader && !showSuccess && (
          <>
            {errorText && (
              <>
                <StyledText color={COLORS.RED} justifySelf="center">
                  {errorText}
                </StyledText>
                <Separator transparent height="25" />
              </>
            )}
            <FieldWrapper>
              {phoneUpdateFormFields.map(field => {
                const { name, editable, required, label: placeholder } = field;
                return (
                  <React.Fragment key={name}>
                    {renderFormField({
                      ...field,
                      value: form[field.name],
                      onChange: this[getOnChangeMethod(name)],
                      required,
                      disabled: !editable,
                      placeholder,
                    })}
                  </React.Fragment>
                );
              })}
            </FieldWrapper>
            <StyledButton
              onClick={this.handlePatchAction}
              label="Update"
              disabled={isButtonDisabled}
            />
          </>
        )}
      </Wrapper>
    );
  }
}

export default connect(
  ({ phoneUpdate }) => ({ phoneUpdate }),
  dispatch => ({
    actions: bindActionCreators(
      {
        updatePhoneNumber,
        closeModal,
      },
      dispatch,
    ),
  }),
)(EditStudentPhoneContainer);
